import React, { useState, useEffect, useCallback } from 'react';
import '../styles/CRMFieldMapping.css';
import { Dropdown, Button } from './';
import { cloudFunctionV2Auth } from '../functions';

const COMMON_FIELDS = [
    { key: 'Bedrooms', label: 'Bedrooms' },
    { key: 'Bathrooms', label: 'Bathrooms' },
    { key: 'Square Footage', label: 'Square Footage' },
    { key: 'Year Built', label: 'Year Built' },
    { key: 'Price', label: 'Price' },
    { key: 'Image', label: 'Image' },
    { key: 'clozers_opportunity_id', label: 'Clozers Opportunity ID' },
    { key: 'Occupancy', label: 'Occupancy' },
    { key: 'Condition', label: 'Condition' },
    { key: 'ARV', label: 'ARV' },
    { key: 'Repair Cost', label: 'Repair Cost' },
];

const SF_EXTRA_FIELDS = [
    { key: 'Address', label: 'Address' },
    { key: 'Garage', label: 'Garage' },
    { key: 'Basement', label: 'Basement' },
    { key: 'Basement Square Footage', label: 'Basement Square Footage' },
];

// GHL opportunities also receive four address fields plus two co-living return
// fields (see src/utils/buildOpportunityCustomFields.js and the auto-provisioner
// functions/flows/ghl/provisionOpportunityFields.js). They must appear in the
// GHL mapping UI: saveCRMFieldMapping replaces the whole mapping object, so
// omitting them here would drop the provisioned fields on a manual save.
const GHL_EXTRA_FIELDS = [
    { key: 'Street Address', label: 'Street Address' },
    { key: 'City', label: 'City' },
    { key: 'State', label: 'State' },
    { key: 'Zip Code', label: 'Zip Code' },
    { key: 'Cash Flow', label: 'Cash Flow' },
    { key: 'Cash on Cash Return', label: 'Cash on Cash Return' },
];

function CRMFieldMapping({ companyId, crmType }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [crmFields, setCrmFields] = useState([]);
    const [mapping, setMapping] = useState({});
    const [openDropdown, setOpenDropdown] = useState(null);
    const [message, setMessage] = useState(null);

    const clozersFields = crmType === 'salesforce'
        ? [...COMMON_FIELDS, ...SF_EXTRA_FIELDS]
        : crmType === 'ghl'
            ? [...COMMON_FIELDS, ...GHL_EXTRA_FIELDS]
            : COMMON_FIELDS;

    const fetchFields = useCallback(async () => {
        const url = process.env.REACT_APP_GET_CRM_CUSTOM_FIELDS;
        if (!url || !companyId || !crmType) return;

        setLoading(true);
        setMessage(null);
        try {
            const result = await cloudFunctionV2Auth(url, { companyId, crmType });
            if (result?.status === 200 && result.data) {
                setCrmFields(result.data.fields || []);
                if (result.data.currentMapping) {
                    setMapping(result.data.currentMapping);
                }
            } else {
                setMessage({ type: 'error', text: result?.error || 'Failed to load CRM fields' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to load CRM fields' });
        } finally {
            setLoading(false);
        }
    }, [companyId, crmType]);

    useEffect(() => {
        fetchFields();
    }, [fetchFields]);

    const handleSelect = (clozersKey, crmField) => {
        setMapping(prev => ({
            ...prev,
            [clozersKey]: crmField ? crmField.apiName : null
        }));
        setOpenDropdown(null);
    };

    const handleSave = async () => {
        const url = process.env.REACT_APP_SAVE_CRM_FIELD_MAPPING;
        if (!url) return;

        setSaving(true);
        setMessage(null);
        try {
            const result = await cloudFunctionV2Auth(url, { companyId, crmType, mapping });
            if (result?.status === 200) {
                setMessage({ type: 'success', text: 'Field mapping saved' });
            } else {
                setMessage({ type: 'error', text: result?.error || 'Failed to save mapping' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to save mapping' });
        } finally {
            setSaving(false);
        }
    };

    const getSelectedLabel = (clozersKey) => {
        const selectedApiName = mapping[clozersKey];
        if (!selectedApiName) return 'Not mapped';
        const field = crmFields.find(f => f.apiName === selectedApiName);
        return field ? field.label : selectedApiName;
    };

    if (loading) {
        return (
            <div className="crm-field-mapping-container">
                <h3 className="heading-small-bold colour-primary">Custom Field Mapping</h3>
                <p className="body-regular colour-secondary margin-top-small">Loading CRM fields...</p>
            </div>
        );
    }

    if (crmFields.length === 0) {
        return (
            <div className="crm-field-mapping-container">
                <h3 className="heading-small-bold colour-primary">Custom Field Mapping</h3>
                <p className="body-regular colour-secondary margin-top-small">
                    No custom fields found in your CRM. Create custom fields on your {crmType === 'ghl' ? 'GoHighLevel' : crmType === 'fub' ? 'Follow Up Boss' : 'Salesforce'} opportunities first, then return here to map them.
                </p>
            </div>
        );
    }

    return (
        <div className="crm-field-mapping-container">
            <div className="crm-field-mapping-header">
                <h3 className="heading-small-bold colour-primary">Custom Field Mapping</h3>
            </div>
            <p className="body-regular colour-secondary margin-x-small">
                Map your CRM's custom fields to Clozers data. Mapped fields will be populated when creating opportunities.
            </p>
            <div className="crm-field-mapping-rows">
                {clozersFields.map((field) => {
                    const isOpen = openDropdown === field.key;
                    const dropdownOptions = [
                        { label: 'Not mapped', value: null, selected: !mapping[field.key] },
                        ...crmFields.map(cf => ({
                            label: cf.label,
                            value: cf.apiName,
                            selected: mapping[field.key] === cf.apiName,
                        }))
                    ];

                    return (
                        <div className="crm-field-mapping-row" key={field.key}>
                            <div className="crm-field-mapping-label">
                                <span className="body-regular colour-primary">{field.label}</span>
                            </div>
                            <div className="crm-field-mapping-dropdown">
                                <Dropdown
                                    open={isOpen}
                                    icon={null}
                                    label={getSelectedLabel(field.key)}
                                    disabled={false}
                                    options={dropdownOptions}
                                    multiselect={false}
                                    cta={(item) => handleSelect(field.key, item.value ? { apiName: item.value } : null)}
                                    sidealign="left"
                                    topalign="bottom"
                                    toggleDropdown={() => setOpenDropdown(isOpen ? null : field.key)}
                                    indexSelect={false}
                                    selectedIndex={null}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="crm-field-mapping-actions">
                <Button
                    buttonType="primary-button"
                    size="medium"
                    text="Save Mapping"
                    cta={handleSave}
                    disabled={saving}
                    leftIcon={null}
                    rightIcon={null}
                    alt=""
                    standardIcon={false}
                    badge={false}
                    badgeVal={null}
                    badgeBG={null}
                    buttonRef={null}
                    spinner={saving}
                />
            </div>
            {message && (
                <div className="crm-field-mapping-message">
                    <p className={`body-regular ${message.type === 'error' ? 'colour-error' : 'colour-green'}`}>
                        {message.text}
                    </p>
                </div>
            )}
        </div>
    );
}

export default CRMFieldMapping;
